/**
 * Format currency for display
 * Uses Kenyan locale (en-KE) with no decimal places
 *
 * @param amount - The numeric amount to format
 * @param currency - The currency code (default: 'KES')
 * @returns Formatted currency string (e.g., "KES 1,500,000")
 */
export const formatCurrency = (amount: number, currency: string = 'KES'): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * CURR-03: `1,500,000` — a bare KES amount with comma thousands separators.
 *
 * A plain `n.toLocaleString()` follows the DEVICE locale, not the site's. On a
 * phone set to a European locale the same property page printed
 * "KES 200 000 000" in the sticky bar and "KES 200 000/m²" under the price
 * while the price bar itself said "Ksh 200,000,000" — three renderings of one
 * currency on one screen, none of them chosen. The locale is pinned here so
 * the separator is a decision rather than a device setting.
 */
export const formatKesAmount = (amount: number): string =>
  Math.round(amount).toLocaleString('en-US');

/**
 * CURR-03: `KES 1,500,000` — the price label for buyer surfaces.
 *
 * `formatCurrency` renders KES through the en-KE locale, which spells the
 * symbol "Ksh". Both spellings are correct Kenyan usage; mixing them on one
 * screen is not, and "KES" is the code the rest of the product (filters, saved
 * searches, the exchange-rate pair) already uses.
 *
 * Only KES is claimed here. A listing priced in anything else falls through to
 * `formatCurrency`, which knows how to spell currencies this one does not.
 */
export const formatListingPrice = (amount: number, currency: string = 'KES'): string => {
  const code = (currency || 'KES').toUpperCase();
  if (code !== 'KES') return formatCurrency(amount, code);

  return `KES ${formatKesAmount(amount)}`;
};
